// src/hooks/useDevices.js
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';

// Klucze zapytań dla spójnego odwoływania się do cache
export const deviceKeys = {
  all: ['devices'],
  allDevices: () => [...deviceKeys.all, 'allDevices'],
};

// Podstawowy hook pobierający wszystkie urządzenia
export function useAllDevices(options = {}) {
  return useQuery({
    queryKey: deviceKeys.allDevices(),
    queryFn: apiService.getDevices,
    staleTime: 5000, // 5 seconds - shorter for real-time device status
    ...options,
  });
}

// Hook filtrujący tylko huby z wszystkich urządzeń
export function useDevices(options = {}) {
  const { data: allDevices = [], isLoading, isError, error, ...rest } = useAllDevices(options);
  
  // Filtruj i formatuj dane po stronie frontu
  const hubs = React.useMemo(() => {
    return allDevices
      .filter(device => device.device_type === 'NestHub' && device.is_authorized === true)
      .map((hub, index) => {
        // Przekonwertuj status połączenia na wartość numeryczną dla StatusBadge
        // 0 = offline/nieaktywny, 1 = online/aktywny
        const connectionStatus = hub.connection_status || { online: false };
        const statusValue = connectionStatus.online ? 1 : 0; 

        return {
          id: index + 1,
          thumb: hub.avatar_base64 ? (
            <img
              src={`data:image/png;base64,${hub.avatar_base64}`}
              alt={`${hub.name} avatar`}
              className="hub-avatar-pixelated"
            />
          ) : (
            <div className="avatar-placeholder">
              {hub.name ? hub.name.charAt(0) : "H"}
            </div>
          ),
          name: hub.name || "Unnamed Hub",
          version: hub.device_version || "N/A",
          os: hub.device_os || "N/A",
          status: statusValue, // Używamy rzeczywistego statusu połączenia
          lastSeen: connectionStatus.last_seen, // Dodajemy informację o ostatnim połączeniu
          uuid: hub.uuid
        };
      });
  }, [allDevices]);
  
  return { data: hubs, isLoading, isError, error, ...rest };
}

// Hook filtrujący oczekujące urządzenia z uwzględnieniem stanów
export function usePendingDevices(options = {}) {
  const { data: allDevices = [], isLoading, isError, error, ...rest } = useAllDevices(options);
  
  // Filtruj tylko nieautoryzowane i dodaj informacje o stanie
  const pendingDevices = React.useMemo(() => {
    return allDevices
      .filter(device => device.is_authorized === false)
      .map(device => ({
        ...device,
        // Określ stan urządzenia na podstawie danych
        deviceState: device.pairing_status || 'pending', // pending, pairing, paired
        isPairing: device.pairing_status === 'pairing',
        isPaired: device.pairing_status === 'paired'
      }));
  }, [allDevices]);
  
  return { data: pendingDevices, isLoading, isError, error, ...rest };
}

// Hook do zatwierdzania urządzenia
export function useApproveDevice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: apiService.approveDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.allDevices() });
    },
  });
}

// Hook do odrzucania urządzenia
export function useRejectDevice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: apiService.rejectDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.allDevices() });
    },
  });
}